import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

// Local interfaces for payment types
interface PaymentBreakdown {
  rental_fee?: number;
  insurance_fee?: number;
  delivery_fee?: number;
  equipment_fee?: number;
  coupon_discount?: number;
}

interface Payment {
  id: number;
  reservation_id: number;
  payment_key: string;
  order_id: string;
  amount: number;
  breakdown?: PaymentBreakdown;
  payment_type: 'initial' | 'extension' | 'additional';
  status: 'pending' | 'approved' | 'completed' | 'failed' | 'cancelled';
  payment_method?: string;
  approved_at?: string;
  receipt_url?: string;
  created_at: string;
}

export const ReservationsListPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(30);
  const [status, setStatus] = useState('');
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [pickupId, setPickupId] = useState<number | null>(null);
  const [revertPickupId, setRevertPickupId] = useState<number | null>(null);
  const [returnId, setReturnId] = useState<number | null>(null);
  const [selectedReservationId, setSelectedReservationId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const { data: reservationsData, isLoading, error } = useQuery({
    queryKey: ['reservationsList', page, limit, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('skip', ((page - 1) * limit).toString());
      params.append('limit', limit.toString());
      if (status) params.append('status', status);

      const response = await apiClient.get<{ total: number; items: any[] }>(`/reservations?${params.toString()}`);
      return response.data;
    },
  });

  const reservations = reservationsData?.items || [];
  const totalCount = reservationsData?.total || 0;

  // 예약 상세 조회 (관리자용 엔드포인트 사용)
  const { data: reservationDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['reservationDetail', selectedReservationId],
    queryFn: async () => {
      if (!selectedReservationId) return null;
      const response = await apiClient.get(`/reservations/admin/${selectedReservationId}`);
      return response.data;
    },
    enabled: !!selectedReservationId,
  });

  // 예약 취소 mutation
  const cancelMutation = useMutation({
    mutationFn: async (reservationId: number) => {
      setCancellingId(reservationId);
      const response = await apiClient.delete(`/reservations/${reservationId}`);
      return response.data;
    },
    onSuccess: () => {
      setCancellingId(null);
      alert('예약이 성공적으로 취소되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['reservationsList'] });
    },
    onError: (error: any) => {
      setCancellingId(null);
      const errorMessage = error?.response?.data?.detail || '예약 취소에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const handleCancelReservation = (reservation: any) => {
    if (!reservation.external_reservation_id) {
      alert('외부 예약 ID가 없어 취소할 수 없습니다.');
      return;
    }

    if (window.confirm(`정말로 이 예약을 취소하시겠습니까?\n\n예약번호: ${reservation.reservation_number}\n차량: ${reservation.vehicle_name}\n고객: ${reservation.user_name}`)) {
      cancelMutation.mutate(reservation.id);
    }
  };

  // 출고 처리 mutation
  const pickupMutation = useMutation({
    mutationFn: async (reservationId: number) => {
      setPickupId(reservationId);
      const response = await apiClient.post(`/reservations/${reservationId}/pickup`);
      return response.data;
    },
    onSuccess: async () => {
      setPickupId(null);
      // 먼저 쿼리를 무효화하고 refetch
      await queryClient.invalidateQueries({ queryKey: ['reservationsList'] });
      await queryClient.invalidateQueries({ queryKey: ['reservationDetail'] });
      alert('출고 처리가 완료되었습니다.');
    },
    onError: (error: any) => {
      setPickupId(null);
      const errorMessage = error?.response?.data?.detail || '출고 처리에 실패했습니다.';
      alert(errorMessage);
    },
  });

  // 출고 취소 mutation
  const revertPickupMutation = useMutation({
    mutationFn: async (reservationId: number) => {
      setRevertPickupId(reservationId);
      const response = await apiClient.post(`/reservations/${reservationId}/revert-pickup`);
      return response.data;
    },
    onSuccess: async () => {
      setRevertPickupId(null);
      // 먼저 쿼리를 무효화하고 refetch
      await queryClient.invalidateQueries({ queryKey: ['reservationsList'] });
      await queryClient.invalidateQueries({ queryKey: ['reservationDetail'] });
      alert('출고가 취소되었습니다. (예약 확정 상태로 복원)');
    },
    onError: (error: any) => {
      setRevertPickupId(null);
      const errorMessage = error?.response?.data?.detail || '출고 취소에 실패했습니다.';
      alert(errorMessage);
    },
  });

  // 반납 처리 mutation
  const returnMutation = useMutation({
    mutationFn: async (reservationId: number) => {
      setReturnId(reservationId);
      const response = await apiClient.post(`/reservations/${reservationId}/return`);
      return response.data;
    },
    onSuccess: async () => {
      setReturnId(null);
      // 먼저 쿼리를 무효화하고 refetch
      await queryClient.invalidateQueries({ queryKey: ['reservationsList'] });
      await queryClient.invalidateQueries({ queryKey: ['reservationDetail'] });
      alert('반납 처리가 완료되었습니다.');
    },
    onError: (error: any) => {
      setReturnId(null);
      const errorMessage = error?.response?.data?.detail || '반납 처리에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const handlePickup = (reservation: any) => {
    const startDate = new Date(reservation.start_date).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const pickupLocation = reservation.pickup_location || '본점';

    if (window.confirm(`출고 처리하시겠습니까?\n\n예약번호: ${reservation.reservation_number}\n차량: ${reservation.vehicle_name}\n고객: ${reservation.user_name}\n대여 시작: ${startDate}\n대여 위치: ${pickupLocation}`)) {
      pickupMutation.mutate(reservation.id);
    }
  };

  const handleRevertPickup = (reservation: any) => {
    if (window.confirm(`출고를 취소하시겠습니까?\n\n예약번호: ${reservation.reservation_number}\n차량: ${reservation.vehicle_name}\n고객: ${reservation.user_name}\n\n※ 예약 확정 상태로 되돌아갑니다.`)) {
      revertPickupMutation.mutate(reservation.id);
    }
  };

  const handleReturn = (reservation: any) => {
    const endDate = new Date(reservation.end_date).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const returnLocation = reservation.return_location || '본점';

    if (window.confirm(`반납 처리하시겠습니까?\n\n예약번호: ${reservation.reservation_number}\n차량: ${reservation.vehicle_name}\n고객: ${reservation.user_name}\n반납 예정: ${endDate}\n반납 위치: ${returnLocation}`)) {
      returnMutation.mutate(reservation.id);
    }
  };

  return (
    <Layout>
      <div className="px-2 py-4">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">예약 조회</h1>

        <div className="flex gap-3">
          {/* Left Panel - Reservation List */}
          <div className="w-2/3">
            {/* Filter Form */}
            <div className="bg-white border border-slate-200 rounded-lg px-3 py-3 mb-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    예약 상태
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setStatus('');
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-md text-sm transition-colors ${
                        status === ''
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      전체
                    </button>
                    <button
                      onClick={() => {
                        setStatus('pending');
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-md text-sm transition-colors ${
                        status === 'pending'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      대기중
                    </button>
                    <button
                      onClick={() => {
                        setStatus('confirmed');
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-md text-sm transition-colors ${
                        status === 'confirmed'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      확정
                    </button>
                    <button
                      onClick={() => {
                        setStatus('in_use');
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-md text-sm transition-colors ${
                        status === 'in_use'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      이용중
                    </button>
                    <button
                      onClick={() => {
                        setStatus('completed');
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-md text-sm transition-colors ${
                        status === 'completed'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      이용완료
                    </button>
                    <button
                      onClick={() => {
                        setStatus('cancelled');
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-md text-sm transition-colors ${
                        status === 'cancelled'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      취소
                    </button>
                    <button
                      onClick={() => {
                        setStatus('failed');
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-md text-sm transition-colors ${
                        status === 'failed'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      실패
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  예약 목록
                  {totalCount > 0 && <span className="ml-2 text-sm font-normal text-gray-600">(전체 {totalCount}건)</span>}
                </h2>
                {totalCount > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{page} / {Math.ceil(totalCount / limit)}</span>
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="inline-flex items-center px-2 py-1 rounded border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page * limit >= totalCount}
                      className="inline-flex items-center px-2 py-1 rounded border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-16 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
              <p className="text-gray-700 text-base font-medium">예약 목록 조회 중...</p>
              <p className="mt-2 text-gray-500 text-sm">잠시만 기다려주세요</p>
            </div>
          ) : error ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-rose-600 text-sm">예약 조회에 실패했습니다.</p>
              <p className="text-sm text-gray-500 mt-2">
                {(error as any)?.response?.data?.detail || (error as Error).message}
              </p>
            </div>
          ) : reservations.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <p className="mt-2 text-gray-500">예약 내역이 없습니다.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      예약일시
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      예약번호
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      대여 시작일시
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      차량명
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      고객명
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      상태
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      액션
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reservations.map((reservation: any) => (
                    <tr
                      key={reservation.id}
                      className={`hover:bg-blue-50 cursor-pointer transition-colors ${
                        selectedReservationId === reservation.id ? 'bg-blue-100' : ''
                      }`}
                      onClick={() => setSelectedReservationId(reservation.id)}
                    >
                      <td className="px-3 py-2 text-sm text-gray-500">
                        {new Date(reservation.created_at).toLocaleString('ko-KR', {
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })}
                      </td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">
                        <div className="flex flex-col">
                          <span className="font-semibold">{reservation.reservation_number}</span>
                          {reservation.external_reservation_id && (
                            <span className="text-xs text-gray-500 mt-1">
                              #{reservation.external_reservation_id}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        {new Date(reservation.start_date).toLocaleString('ko-KR', {
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900">
                        <div className="flex flex-col">
                          <span>{reservation.vehicle_name}</span>
                          {reservation.vehicle_number && (
                            <span className="text-xs text-gray-500">{reservation.vehicle_number}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900">
                        <div className="flex flex-col">
                          <span>{reservation.user_name}</span>
                          <span className="text-xs text-gray-500">{reservation.user_phone}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded ${
                          reservation.status === 'confirmed'
                            ? 'bg-green-100 text-green-700'
                            : reservation.status === 'in_use'
                            ? 'bg-blue-100 text-blue-700'
                            : reservation.status === 'completed'
                            ? 'bg-gray-100 text-gray-700'
                            : reservation.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : reservation.status === 'cancelled'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {reservation.status === 'confirmed' ? '확정' :
                           reservation.status === 'in_use' ? '이용중' :
                           reservation.status === 'completed' ? '이용완료' :
                           reservation.status === 'pending' ? '대기중' :
                           reservation.status === 'cancelled' ? '취소' :
                           reservation.status === 'failed' ? '실패' : reservation.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm">
                        {reservation.status === 'confirmed' ? (
                          <div className="flex gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePickup(reservation);
                              }}
                              disabled={pickupId === reservation.id}
                              className="inline-flex items-center justify-center h-7 px-2 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="출고"
                            >
                              {pickupId === reservation.id ? (
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : '출고'}
                            </button>
                            {reservation.external_reservation_id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelReservation(reservation);
                                }}
                                disabled={cancellingId === reservation.id}
                                className="inline-flex items-center justify-center h-7 px-2 rounded text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="취소"
                              >
                                {cancellingId === reservation.id ? (
                                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : '취소'}
                              </button>
                            )}
                          </div>
                        ) : reservation.status === 'in_use' ? (
                          <div className="flex gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRevertPickup(reservation);
                              }}
                              disabled={revertPickupId === reservation.id}
                              className="inline-flex items-center justify-center h-7 px-2 rounded text-xs font-medium bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="출고 취소"
                            >
                              {revertPickupId === reservation.id ? (
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : '예약'}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReturn(reservation);
                              }}
                              disabled={returnId === reservation.id}
                              className="inline-flex items-center justify-center h-7 px-2 rounded text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="반납"
                            >
                              {returnId === reservation.id ? (
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : '반납'}
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-end border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">{page} / {Math.ceil(totalCount / limit)}</span>
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="inline-flex items-center px-2 py-1 rounded border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page * limit >= totalCount}
                    className="inline-flex items-center px-2 py-1 rounded border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
            </div>
          </div>

          {/* Right Panel - Reservation Detail */}
          <div className="w-1/3">
            <div className="bg-white rounded-lg border border-slate-200 sticky top-0 max-h-[calc(100vh-8rem)] overflow-y-auto">
              {isLoadingDetail ? (
                <div className="flex items-center justify-center h-full p-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
                    <p className="text-gray-600 text-sm">상세 정보 로딩 중...</p>
                  </div>
                </div>
              ) : selectedReservationId && reservationDetail ? (
                <>
                  {/* 헤더 */}
                  <div className="sticky top-0 bg-white border-b px-5 py-4 flex justify-between items-center z-10 rounded-t-lg">
                    <h2 className="text-xl font-bold text-gray-900">예약 상세 정보</h2>
                    <button
                      onClick={() => setSelectedReservationId(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                {/* 내용 */}
                <div className="p-5 space-y-4">
                  {/* 예약 기본 정보 */}
                  <div>
                    <h3 className="text-base font-semibold mb-3 text-gray-900">예약 정보</h3>
                    <div className="grid grid-cols-2 gap-3 bg-gray-50 p-3 rounded-lg text-sm">
                      <div>
                        <span className="text-xs text-gray-600">예약번호</span>
                        <p className="font-medium">{reservationDetail.reservation_number}</p>
                      </div>
                      {reservationDetail.external_reservation_id && (
                        <div>
                          <span className="text-xs text-gray-600">외부 예약 ID</span>
                          <p className="font-medium">{reservationDetail.external_reservation_id}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-xs text-gray-600">예약 상태</span>
                        <p>
                          <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded ${
                            reservationDetail.status === 'confirmed'
                              ? 'bg-green-100 text-green-700'
                              : reservationDetail.status === 'in_use'
                              ? 'bg-blue-100 text-blue-700'
                              : reservationDetail.status === 'completed'
                              ? 'bg-gray-100 text-gray-700'
                              : reservationDetail.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : reservationDetail.status === 'cancelled'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {reservationDetail.status === 'confirmed' ? '확정' :
                             reservationDetail.status === 'in_use' ? '이용중' :
                             reservationDetail.status === 'completed' ? '이용완료' :
                             reservationDetail.status === 'pending' ? '대기중' :
                             reservationDetail.status === 'cancelled' ? '취소' :
                             reservationDetail.status === 'failed' ? '실패' : reservationDetail.status}
                          </span>
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">예약 일시</span>
                        <p className="font-medium text-sm">{new Date(reservationDetail.created_at).toLocaleString('ko-KR', { hour12: false })}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">예약 경로</span>
                        <p>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            reservationDetail.reservation_source === 'admin'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {reservationDetail.reservation_source === 'admin' ? '관리자 예약' : '고객 예약'}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* 고객 정보 */}
                  <div>
                    <h3 className="text-base font-semibold mb-3 text-gray-900">고객 정보</h3>
                    <div className="grid grid-cols-2 gap-3 bg-gray-50 p-3 rounded-lg text-sm">
                      <div>
                        <span className="text-xs text-gray-600">이름</span>
                        <p className="font-medium">{reservationDetail.user_name}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">연락처</span>
                        <p className="font-medium">{reservationDetail.user_phone}</p>
                      </div>
                      {reservationDetail.user_birth_date && (
                        <div>
                          <span className="text-xs text-gray-600">생년월일</span>
                          <p className="font-medium">{new Date(reservationDetail.user_birth_date).toLocaleDateString('ko-KR')}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-xs text-gray-600">이메일</span>
                        <p className="font-medium text-sm">{reservationDetail.user_email}</p>
                      </div>
                    </div>
                  </div>

                  {/* 차량 정보 */}
                  <div>
                    <h3 className="text-base font-semibold mb-3 text-gray-900">차량 정보</h3>
                    <div className="grid grid-cols-2 gap-3 bg-gray-50 p-3 rounded-lg text-sm">
                      <div>
                        <span className="text-xs text-gray-600">차량명</span>
                        <p className="font-medium">{reservationDetail.vehicle_name}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">차량 등급</span>
                        <p className="font-medium">{reservationDetail.vehicle_class}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">차량번호</span>
                        <p className="font-medium">{reservationDetail.vehicle_number || '-'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">차량 ID</span>
                        <p className="font-medium">{reservationDetail.vehicle_idx}</p>
                      </div>
                    </div>
                  </div>

                  {/* 대여 정보 */}
                  <div>
                    <h3 className="text-base font-semibold mb-3 text-gray-900">대여 정보</h3>
                    <div className="grid grid-cols-2 gap-3 bg-gray-50 p-3 rounded-lg text-sm">
                      <div>
                        <span className="text-xs text-gray-600">대여 시작</span>
                        <p className="font-medium text-sm">{new Date(reservationDetail.start_date).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">반납 예정</span>
                        <p className="font-medium text-sm">{new Date(reservationDetail.end_date).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}</p>
                      </div>
                      {reservationDetail.pickup_location && (
                        <div>
                          <span className="text-xs text-gray-600">대여 위치</span>
                          <p className="font-medium text-sm">{reservationDetail.pickup_location}</p>
                        </div>
                      )}
                      {reservationDetail.return_location && (
                        <div>
                          <span className="text-xs text-gray-600">반납 위치</span>
                          <p className="font-medium text-sm">{reservationDetail.return_location}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 결제 정보 */}
                  <div>
                    <h3 className="text-base font-semibold mb-3 text-gray-900">결제 정보</h3>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="mb-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600 font-medium">총 결제 금액</span>
                          <span className="text-lg font-bold text-blue-600">
                            {Math.floor(reservationDetail.total_amount || 0).toLocaleString()}원
                          </span>
                        </div>
                      </div>

                      {/* 모든 결제 내역 표시 */}
                      {reservationDetail.payments && reservationDetail.payments.length > 0 ? (
                        <div className="space-y-3">
                          <p className="text-xs font-semibold text-gray-700">결제 내역</p>
                          {reservationDetail.payments.map((payment: Payment, index: number) => (
                            <div key={payment.id || index} className="bg-white rounded-lg p-3 border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-gray-700">
                                  {payment.payment_type === 'initial' && '초기 결제'}
                                  {payment.payment_type === 'extension' && '연장 결제'}
                                  {payment.payment_type === 'additional' && '추가 결제'}
                                </span>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  payment.status === 'completed' || payment.status === 'approved'
                                    ? 'bg-green-100 text-green-800'
                                    : payment.status === 'failed'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {payment.status === 'completed' || payment.status === 'approved' ? '결제완료' :
                                   payment.status === 'failed' ? '실패' : payment.status}
                                </span>
                              </div>

                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">결제금액</span>
                                  <span className="font-semibold text-gray-900">{Math.floor(Number(payment.amount)).toLocaleString()}원</span>
                                </div>

                                {payment.payment_method && (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">결제수단</span>
                                    <span className="text-gray-700">{payment.payment_method}</span>
                                  </div>
                                )}

                                {payment.approved_at && (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">승인시각</span>
                                    <span className="text-gray-700">{new Date(payment.approved_at).toLocaleString('ko-KR', { hour12: false })}</span>
                                  </div>
                                )}

                                {payment.order_id && (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">주문번호</span>
                                    <span className="text-gray-700 font-mono break-all">{payment.order_id}</span>
                                  </div>
                                )}

                                {/* 결제 세부 항목 */}
                                {payment.breakdown && (
                                  <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                                    {payment.breakdown.rental_fee !== undefined && payment.breakdown.rental_fee > 0 && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-gray-500">대여료</span>
                                        <span className="text-gray-700">{Math.floor(payment.breakdown.rental_fee).toLocaleString()}원</span>
                                      </div>
                                    )}
                                    {payment.breakdown.insurance_fee !== undefined && payment.breakdown.insurance_fee > 0 && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-gray-500">보험료</span>
                                        <span className="text-gray-700">{Math.floor(payment.breakdown.insurance_fee).toLocaleString()}원</span>
                                      </div>
                                    )}
                                    {payment.breakdown.delivery_fee !== undefined && payment.breakdown.delivery_fee > 0 && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-gray-500">배송료</span>
                                        <span className="text-gray-700">{Math.floor(payment.breakdown.delivery_fee).toLocaleString()}원</span>
                                      </div>
                                    )}
                                    {payment.breakdown.equipment_fee !== undefined && payment.breakdown.equipment_fee > 0 && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-gray-500">장비료</span>
                                        <span className="text-gray-700">{Math.floor(payment.breakdown.equipment_fee).toLocaleString()}원</span>
                                      </div>
                                    )}
                                    {payment.breakdown.coupon_discount !== undefined && payment.breakdown.coupon_discount > 0 && (
                                      <div className="flex justify-between text-xs">
                                        <span className="text-gray-500">쿠폰 할인</span>
                                        <span className="text-red-600">-{Math.floor(payment.breakdown.coupon_discount).toLocaleString()}원</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : reservationDetail.payment ? (
                        <div className="space-y-2 text-sm">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <span className="text-xs text-gray-600">결제 방법</span>
                              <p className="font-medium">{reservationDetail.payment.payment_method || '-'}</p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-600">결제 상태</span>
                              <p className="font-medium">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  reservationDetail.payment.status === 'approved'
                                    ? 'bg-green-100 text-green-800'
                                    : reservationDetail.payment.status === 'failed'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {reservationDetail.payment.status === 'approved' ? '승인' :
                                   reservationDetail.payment.status === 'failed' ? '실패' :
                                   reservationDetail.payment.status}
                                </span>
                              </p>
                            </div>
                            <div className="col-span-2">
                              <span className="text-xs text-gray-600">주문 ID</span>
                              <p className="font-medium text-xs break-all">{reservationDetail.payment.order_id}</p>
                            </div>
                            {reservationDetail.payment.approved_at && (
                              <div className="col-span-2">
                                <span className="text-xs text-gray-600">승인 일시</span>
                                <p className="font-medium text-xs">{new Date(reservationDetail.payment.approved_at).toLocaleString('ko-KR', { hour12: false })}</p>
                              </div>
                            )}
                            {(reservationDetail.payment.failure_reason || reservationDetail.payment.error_message) && (
                              <div className="col-span-2">
                                <span className="text-xs text-gray-600">실패 사유</span>
                                <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded">
                                  <p className="font-medium text-xs text-red-800">
                                    {reservationDetail.payment.failure_reason || reservationDetail.payment.error_message}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* 결제 세부 항목 */}
                          {reservationDetail.payment.breakdown && (
                            <div className="pt-3 border-t border-gray-200">
                              <p className="text-xs text-gray-600 mb-2 font-medium">결제 내역</p>
                              <div className="space-y-1.5">
                                {reservationDetail.payment.breakdown.rental_fee !== undefined && (
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-700">대여료</span>
                                    <span className="font-medium">{Math.floor(reservationDetail.payment.breakdown.rental_fee).toLocaleString()}원</span>
                                  </div>
                                )}
                                {reservationDetail.payment.breakdown.insurance_fee !== undefined && (
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-700">보험료</span>
                                    <span className="font-medium">{Math.floor(reservationDetail.payment.breakdown.insurance_fee).toLocaleString()}원</span>
                                  </div>
                                )}
                                {reservationDetail.payment.breakdown.delivery_fee !== undefined && (
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-700">배송료</span>
                                    <span className="font-medium">{Math.floor(reservationDetail.payment.breakdown.delivery_fee).toLocaleString()}원</span>
                                  </div>
                                )}
                                {reservationDetail.payment.breakdown.equipment_fee !== undefined && reservationDetail.payment.breakdown.equipment_fee > 0 && (
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-700">장비료</span>
                                    <span className="font-medium">{Math.floor(reservationDetail.payment.breakdown.equipment_fee).toLocaleString()}원</span>
                                  </div>
                                )}
                                {reservationDetail.user_coupon && (
                                  <div className="flex justify-between items-center text-sm text-green-600">
                                    <span>쿠폰 할인</span>
                                    <span className="font-medium">-{Math.floor(reservationDetail.coupon_discount || 0).toLocaleString()}원</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="pt-2 border-t border-gray-200">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600 font-medium">총 결제 금액</span>
                              <span className="text-lg font-bold text-blue-600">
                                {Math.floor(reservationDetail.total_amount || 0).toLocaleString()}원
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="text-center py-4 text-gray-500 text-sm">결제 정보가 없습니다</div>

                          {/* API 연동 실패 또는 예약 실패 메시지 */}
                          {reservationDetail.status === 'failed' && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                              <div className="flex items-start">
                                <svg className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-red-800 mb-1">예약 처리 실패</p>
                                  <p className="text-xs text-red-700">
                                    {reservationDetail.failure_reason ||
                                     reservationDetail.error_message ||
                                     '예약 처리 중 오류가 발생했습니다. API 연동 또는 결제 과정에서 문제가 발생했을 수 있습니다.'}
                                  </p>
                                  {reservationDetail.external_error && (
                                    <div className="mt-2 p-2 bg-white rounded border border-red-300">
                                      <p className="text-xs text-gray-600 font-medium mb-1">외부 API 오류:</p>
                                      <p className="text-xs text-gray-700 font-mono break-all">{reservationDetail.external_error}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 특별 요청 사항 */}
                  {reservationDetail.special_request && (
                    <div>
                      <h3 className="text-base font-semibold mb-3 text-gray-900">특별 요청 사항</h3>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-gray-700 text-sm">{reservationDetail.special_request}</p>
                      </div>
                    </div>
                  )}

                  {/* 리뷰 정보 */}
                  {reservationDetail.review ? (
                    <div>
                      <h3 className="text-base font-semibold mb-3 text-gray-900">고객 리뷰</h3>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center mb-2">
                          <div className="flex items-center">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <svg
                                key={star}
                                className={`w-5 h-5 ${
                                  star <= (reservationDetail.review?.rating || 0)
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                                />
                              </svg>
                            ))}
                            <span className="ml-2 text-sm font-medium text-gray-700">
                              {reservationDetail.review.rating}.0
                            </span>
                          </div>
                          <span className="ml-auto text-xs text-gray-500">
                            {new Date(reservationDetail.review.created_at).toLocaleString('ko-KR', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            })}
                          </span>
                        </div>
                        <p className="text-gray-700 text-sm whitespace-pre-wrap">{reservationDetail.review.content}</p>
                      </div>
                    </div>
                  ) : reservationDetail.status === 'completed' && (
                    <div>
                      <h3 className="text-base font-semibold mb-3 text-gray-900">고객 리뷰</h3>
                      <div className="bg-gray-50 p-3 rounded-lg text-center">
                        <p className="text-gray-500 text-sm">아직 작성된 리뷰가 없습니다.</p>
                      </div>
                    </div>
                  )}
                </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full p-12">
                  <div className="text-center">
                    <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h3 className="mt-4 text-lg font-medium text-gray-900">예약을 선택해주세요</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      왼쪽 목록에서 예약을 클릭하면<br/>
                      상세 정보가 여기에 표시됩니다
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
